import styled from 'styled-components';
import { palette, theme } from 'styled-tools';

import { FontSizes, FontWeights, minWidth, Text, units } from '@calm-web/design-system';

import Image from '@/components/image/NextImage';
import { COLLAPSED_AUDIO_PLAYER_INDEX, GUIDE_INFO_INDEX } from '@/utils/ui/zIndices';

// Main AudioPlayer styles
export const AudioPlayerWrapper = styled.div`
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1000;
`;

export const Wrapper = styled.div<{ isVisible: boolean }>`
	bottom: 0;
	height: calc(min(68px, 20vh));
	opacity: ${p => (p.isVisible ? '1' : '0')};
	left: 0;
	position: fixed;
	right: 0;
	transform: translate3d(0, ${p => (p.isVisible ? '0' : '100%')}, 0);
	transition: ${p => (p.isVisible ? theme('animation.transition') : 'none')};
	visibility: ${p => (p.isVisible ? 'visible' : 'hidden')};
	z-index: ${COLLAPSED_AUDIO_PLAYER_INDEX};
	${minWidth('tablet')} {
		height: 100px;
	}
`;

export const Background = styled.div`
	background: ${palette('darkBlue')};
	bottom: 0;
	box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
	left: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	top: 0;
`;

export const Container = styled.div`
	align-items: center;
	bottom: 0;
	display: flex;
	height: 100%;
	left: 0;
	padding: 8px 16px;
	position: absolute;
	right: 0;
	overflow-y: hidden;
	${minWidth('tablet')} {
		height: 100px;
		padding: 8px 32px;
	}
`;

export const GuideInfo = styled.div<{ isVisible: boolean }>`
	align-items: center;
	color: ${palette('white')};
	display: flex;
	flex: 1 1 80%;
	opacity: ${p => (p.isVisible ? '1' : '0')};
	pointer-events: none;
	transition: ${p => (p.isVisible ? theme('animation.transition') : 'none')};
	z-index: ${GUIDE_INFO_INDEX};
	margin-right: ${units(1)};
	max-height: 100%;
	${minWidth('tablet')} {
		flex: 1 0 258px;
	}
`;

export const GuideThumbnail = styled(Image)`
	border-radius: 50%;
`;

export const GuideThumbnailContainer = styled.div`
	flex: 0 1 32px;
	${minWidth('tablet')} {
		flex-basis: 64px;
	}
`;

export const GuideCopy = styled.div`
	margin-left: 8px;
	${minWidth('tablet')} {
		margin-left: 16px;
	}
	align-self: stretch;
	display: flex;
	align-items: normal;
	flex-direction: column;
	justify-content: center;
`;

export const GuideTitle = styled(Text).attrs({
	el: 'h2',
	weight: FontWeights.Regular,
	size: FontSizes.base,
	noMargin: true,
})`
	${minWidth('tablet')} {
		font-size: inherit;
	}
`;

export const ProgramTitle = styled.div`
	opacity: 0.8;
	font-size: 12px;
	font-weight: 500;
	text-transform: uppercase;
	display: none;
	${minWidth('mobileLarge')} {
		display: block;
	}
	${minWidth('tablet')} {
		font-size: inherit;
	}
`;

export const InvisibleSpacer = styled.div`
	display: none;
	flex: 0 0 24px;
	${minWidth('tablet')} {
		display: block;
		flex: 1 0 24px;
		text-align: right;
	}
`;

// Transport styles
export const TransportWrapper = styled.div`
	align-items: center;
	display: flex;
	flex-direction: row;
	justify-content: center;
	color: ${palette('white')};
	height: 100%;
	margin: 0;
	text-align: center;
	width: 100%;
	flex: 0 0 20%;
	${minWidth('tablet')} {
		height: auto;
		display: block;
		margin-left: 0;
		flex: 0 0 368px;
		width: 368px;
	}
`;

export const TransportIcons = styled.div`
	align-items: center;
	display: none;
	margin: 0 auto;
	justify-content: center;
	${minWidth('tablet')} {
		display: flex;
		margin: 0 auto;
		justify-content: center;
	}
`;

export const MobileTransportIcons = styled.div`
	align-items: center;
	display: flex;
	height: 48px;
	justify-content: center;
	margin: 0 auto;
	width: 48px;
	${minWidth('tablet')} {
		display: none;
	}
`;

export const SeekBarContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	${minWidth('tablet')} {
		margin-top: 12px;
		flex-direction: row;
		justify-content: center;
		margin-top: 0;
	}
`;

export const Time = styled.div`
	font-size: 12px;
	text-align: center;
	margin-bottom: 6px;
	&:last-of-type {
		display: none;
		${minWidth('tablet')} {
			display: block;
		}
	}
	${minWidth('tablet')} {
		font-size: 15px;
		text-align: left;
		width: 72px;
		margin-top: 0;
	}
	&:last-of-type {
		text-align: right;
	}
`;

// SeekBar styles
export const SeekBarWrapper = styled.div`
	flex: 1;
	margin: 0 16px;
	min-width: 200px;
`;

export const ProgressBar = styled.input`
	width: 100%;
	height: 2px;
	border-radius: 1px;
	background: ${palette('white')};
	outline: none;
	-webkit-appearance: none;
	cursor: pointer;
	position: relative;

	&::-webkit-slider-runnable-track {
		background: ${palette('white')};
		height: 2px;
		width: 100%;
	}
	&::-webkit-slider-thumb {
		-webkit-appearance: none;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #3bbeec;
		cursor: pointer;
		border: 2px solid #3bbeec;
		box-sizing: content-box;
		position: relative;
		top: 50%;
		transform: translateY(-50%);
	}
	&::-moz-range-thumb {
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #3bbeec;
		cursor: pointer;
		border: none;
		appearance: none;
		margin: 0;
	}
	&::-moz-range-track {
		background: ${palette('white')};
		height: 2px;
	}
	&::-moz-range-progress {
		background: #3bbeec;
		height: 2px;
	}
	&::-ms-thumb {
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #3bbeec;
		cursor: pointer;
		border: none;
	}
	&::-ms-fill-lower {
		background: #3bbeec;
	}
	&::-ms-fill-upper {
		background: ${palette('white')};
	}
`;

// TransportIcon styles
export const TransportIconWrapper = styled.div<{ $isPrimary?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 4px;
`;

export const StyledIconButton = styled.button<{ $isPrimary?: boolean }>`
	background: transparent;
	color: ${palette('white')};
	border: none;
	border-radius: 0;
	width: ${p => (p.$isPrimary ? '48px' : '40px')};
	height: ${p => (p.$isPrimary ? '48px' : '40px')};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	&:hover {
		opacity: 0.8;
	}
	svg {
		width: ${p => (p.$isPrimary ? '32px' : '24px')};
		height: ${p => (p.$isPrimary ? '32px' : '24px')};
		fill: ${palette('white')};
		color: ${palette('white')};
	}
`;
